import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './CameraCapture.css';

/**
 * CameraCapture - Full-screen camera overlay using getUserMedia
 * Features:
 * - Live camera preview
 * - Capture photo button
 * - Flip camera (front/back) on mobile
 * - Close button
 */
const CameraCapture = ({ open, onCapture, onClose, initialFacingMode = 'environment' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState(initialFacingMode); // 'environment' = back, 'user' = front
  const requestRef = useRef(0);
  const [cameraError, setCameraError] = useState(null);
  const [isStarting, setIsStarting] = useState(true);

  const stopCamera = useCallback(() => {
    ++requestRef.current;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  // Start/restart camera stream
  const startCamera = useCallback(async (facing) => {
    const request = ++requestRef.current;
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsStarting(true);
    setCameraError(null);

    try {
      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (request !== requestRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsStarting(false);
        };
      }
    } catch (err) {
      if (request !== requestRef.current) return;
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera permissions.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Could not access camera. Please try again.');
      }
      setIsStarting(false);
    }
  }, []);

  // Start camera when opened
  useEffect(() => {
    if (open) {
      startCamera(facingMode);
    }

    return stopCamera;
  }, [open, facingMode, startCamera, stopCamera]);

  // Flip camera
  const handleFlipCamera = useCallback(() => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);

  }, [facingMode]);

  // Capture photo
  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    // Mirror the image if using front camera
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        handleClose();
        onCapture(file);
      }
    }, 'image/jpeg', 0.92);
  }, [facingMode, onCapture]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close and cleanup
  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [onClose, stopCamera]);

  if (!open) return null;

  const cameraContent = (
    <div className="camera-capture-overlay" role="dialog" aria-modal="true" aria-label="Take photo">
      {/* Video preview */}
      <video
        ref={videoRef}
        className={`camera-video ${facingMode === 'user' ? 'mirrored' : ''}`}
        autoPlay
        playsInline
        muted
      />

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Loading state */}
      {isStarting && !cameraError && (
        <div className="camera-loading">
          <div className="camera-spinner" />
          <span>Starting camera...</span>
        </div>
      )}

      {/* Error state */}
      {cameraError && (
        <div className="camera-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <line x1="9" y1="15" x2="15" y2="9" />
            <line x1="15" y1="15" x2="9" y2="9" />
          </svg>
          <p>{cameraError}</p>
          <button className="camera-retry-btn" onClick={() => startCamera(facingMode)}>
            Try Again
          </button>
        </div>
      )}

      {/* Close button */}
      <button className="camera-close-btn" aria-label="Close camera" type="button" onClick={handleClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Bottom controls */}
      <div className="camera-controls">
        {/* Spacer for centering */}
        <div className="camera-control-spacer" />

        {/* Capture button */}
        <button
          className="camera-capture-btn"
          aria-label="Capture photo"
          type="button"
          onClick={handleCapture}
          disabled={isStarting || !!cameraError}
        >
          <div className="camera-capture-btn-inner" />
        </button>

        {/* Flip camera button */}
        {(
          <button className="camera-flip-btn" aria-label="Flip camera" title="Flip camera" type="button" disabled={isStarting} onClick={handleFlipCamera}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <polyline points="23 20 23 14 17 14" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10" />
              <path d="M3.51 15A9 9 0 0 0 18.36 18.36L23 14" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(cameraContent, document.body);
};

export default CameraCapture;
